/* eslint-disable */
import _ from 'lodash';

// Categories are user-defined grouping LABELS (not a DB entity). `fields.categories`
// is the ordered list of label strings the builder shows in the left rail; each
// item carries its label in `item.category`. Empty categories live only in edit
// state — on save the backend derives grouping from the items' `category` strings.

const RetailMenuConfig = {
	initialState: {
		fields: {
			id: null,
			name: '',
			description: '',
			imageUrl: '',
			isEnabled: true,
			storeIds: [],
			categories: [],
			items: [],
		},
		errors: { name: '' },
	},

	mandatoryFields: { name: true },

	// API response (getMenu) -> reducer fields.
	serializer: (data) => {
		const items = (data?.items ?? []).map((it) => ({
			id: it?.id ?? null,
			variantId: it?.variantId,
			category: it?.category ?? '',
			price: it?.price ?? null,
			displayName: it?.displayName ?? '',
			imageUrl: it?.imageUrl ?? '',
			fulfilmentType: it?.fulfilmentType ?? null,
			isEnabled: it?.isEnabled ?? true,
			isAvailable: it?.isAvailable ?? true,
			// display-only, carried through the session (BE ignores unknown keys)
			_productName: it?._productName,
			_variantName: it?._variantName,
			_clientVariantId: it?._clientVariantId,
			_sellingPrice: it?._sellingPrice,
		}));
		// derive categories from items (first-seen order), keeping non-empty labels
		const cats = [];
		items.forEach((it) => {
			const c = it.category || '';
			if (c && !cats.includes(c)) cats.push(c);
		});
		return {
			id: data?.id ?? null,
			name: data?.name ?? '',
			description: data?.description ?? '',
			imageUrl: data?.imageUrl ?? '',
			isEnabled: data?.isEnabled ?? true,
			storeIds: data?.storeIds ?? [],
			categories: cats,
			items,
		};
	},

	// reducer fields -> flat payload the BE aggregate-save expects.
	deserializer: (fields) => ({
		...(fields.id ? { id: fields.id } : {}),
		name: fields.name,
		description: fields.description,
		imageUrl: fields.imageUrl,
		isEnabled: fields.isEnabled,
		storeIds: fields.storeIds ?? [],
		items: (fields.items ?? []).map((it, iIdx) => ({
			...(it.id ? { id: it.id } : {}),
			variantId: it.variantId,
			category: it.category || null,
			price:
				it.price === '' || it.price === null || it.price === undefined
					? null
					: Number(it.price),
			displayName: it.displayName || null,
			imageUrl: it.imageUrl || null,
			fulfilmentType: it.fulfilmentType || null,
			sortOrder: iIdx,
			isEnabled: it.isEnabled,
			isAvailable: it.isAvailable,
		})),
	}),
};

export const menuBuilderReducer = (state, action) => {
	switch (action.type) {
		case 'INIT':
			return { ...state, fields: RetailMenuConfig.serializer(action.payload) };
		case 'SET_FIELD':
			return { ...state, fields: { ...state.fields, [action.name]: action.value } };
		case 'SET_STORES':
			return { ...state, fields: { ...state.fields, storeIds: action.payload ?? [] } };

		case 'ADD_CATEGORY': {
			const name = (action.name || '').trim();
			if (!name || (state.fields.categories || []).includes(name)) return state;
			return _.update(_.cloneDeep(state), 'fields.categories', (c) => [...(c ?? []), name]);
		}
		case 'RENAME_CATEGORY': {
			const to = (action.to || '').trim();
			if (!to) return state;
			const next = _.cloneDeep(state);
			next.fields.categories = (next.fields.categories || []).map((c) =>
				c === action.from ? to : c
			);
			next.fields.items = (next.fields.items || []).map((it) =>
				it.category === action.from ? { ...it, category: to } : it
			);
			return next;
		}
		case 'REMOVE_CATEGORY': {
			const next = _.cloneDeep(state);
			next.fields.categories = (next.fields.categories || []).filter(
				(c) => c !== action.name
			);
			// drop the items that belonged to it
			next.fields.items = (next.fields.items || []).filter(
				(it) => (it.category || '') !== action.name
			);
			return next;
		}

		case 'ADD_ITEMS': {
			// action.items already deduped by the picker against existing variantIds.
			const withCat = (action.items ?? []).map((it) => ({
				...it,
				category: action.category || it.category || '',
			}));
			return _.update(_.cloneDeep(state), 'fields.items', (items) => [
				...(items ?? []),
				...withCat,
			]);
		}
		case 'UPDATE_ITEM': {
			const next = _.cloneDeep(state);
			const patch = action.patch || { [action.key]: action.value };
			next.fields.items[action.itemIndex] = {
				...next.fields.items[action.itemIndex],
				...patch,
			};
			return next;
		}
		case 'REMOVE_ITEM':
			return _.update(_.cloneDeep(state), 'fields.items', (items) =>
				(items ?? []).filter((_it, i) => i !== action.itemIndex)
			);

		case 'RESET':
			return { ...RetailMenuConfig.initialState };
		default:
			return state;
	}
};

export default RetailMenuConfig;
